import {
  formatPhoneNumberToE123,
  getNationalNumberingPlan,
  normalizePhoneNumberToE164,
} from 'src/core/helpers/phone-number-normalization';
import { PhoneNumberDisplayFormat } from 'src/core/models/settings/localization/phone-number-display-format.enum';

/** NSN length a placeholder shows for a country the platform has no numbering plan for. */
const PLACEHOLDER_NATIONAL_LENGTH_WITHOUT_PLAN = 9;

/** Stands for one digit in an input placeholder. */
const PLACEHOLDER_DIGIT = '#';

/**
 * Renders a phone number according to the tenant's `PhoneNumberDisplayFormat`.
 *
 * Parsing and grouping are delegated to the shared phone utilities — the same ones the contact form
 * uses to normalise what an operator types — so a number is displayed exactly the way it is stored
 * and validated, with the tenant setting choosing only *which* of the three renderings is shown.
 * This is display only: it never rewrites a form value.
 *
 * A value that is not a valid E.164 number (an internal extension, a service number, free text) is
 * echoed unchanged rather than regrouped into something misleading.
 *
 * @param value the stored or dialed number.
 * @param format the tenant's phone number display format.
 * @param tenantCountryCode the tenant's default country calling code without a plus, e.g. `90`; it
 * completes national-form input and decides which numbers the `Local` format may rewrite.
 */
export function formatPhoneNumber(
  value: string,
  format: PhoneNumberDisplayFormat,
  tenantCountryCode: string
): string {
  const trimmed = value?.trim() ?? '';

  const e164 = normalizePhoneNumberToE164(trimmed, tenantCountryCode);
  if (!e164) {
    return trimmed;
  }

  if (format === PhoneNumberDisplayFormat.DigitsOnly) {
    return e164.slice(1);
  }

  const international = formatPhoneNumberToE123(e164);

  if (format === PhoneNumberDisplayFormat.Local) {
    return toLocalNumber(e164, international, tenantCountryCode);
  }

  return international;
}

/**
 * Builds the phone input placeholder for the tenant's country, grouped the way the number will be
 * shown after the field is left: `+90 ### ### ## ##`, `+44 ## #### ####`, `+1 ### ### ####`, and
 * `+33 ### ### ###` (generic groups of three) for a country without a numbering plan.
 *
 * @param tenantCountryCode the tenant's default country calling code without a plus, e.g. `90`.
 */
export function buildPhoneNumberPlaceholder(tenantCountryCode: string): string {
  const plan = getNationalNumberingPlan(tenantCountryCode);
  if (plan) {
    return formatPhoneNumberToE123(
      `+${tenantCountryCode}${PLACEHOLDER_DIGIT.repeat(plan.nationalNumberMaxLength)}`
    );
  }

  const groups: string[] = [];
  for (let remaining = PLACEHOLDER_NATIONAL_LENGTH_WITHOUT_PLAN; remaining > 0; remaining -= 3) {
    groups.push(PLACEHOLDER_DIGIT.repeat(Math.min(3, remaining)));
  }

  return `+${tenantCountryCode} ${groups.join(' ')}`;
}

/**
 * Placeholder of a phone input whose value is stored exactly as typed — the inbound and outbound
 * numbers, which are matched to the carrier's digits literally and never normalized to E.164 (unlike
 * a contact's number). It shows the shape these fields accept: digits only, no plus.
 */
export const NATIONAL_PHONE_NUMBER_PLACEHOLDER = '0212...';

/**
 * Rewrites a number of the TENANT's own country in its national form, reusing the international
 * grouping: `+90 532 123 45 67` → `0532 123 45 67`, `+1 415 555 0123` → `1 415 555 0123`.
 *
 * The trunk prefix comes from the tenant country's numbering plan. A trunk `0` is written as part of
 * the area code (the common national convention, and the same digit the international form may show
 * as "(0)"); any other trunk is kept as a group of its own.
 *
 * The international form is kept for numbers of other countries, for a tenant country without a
 * numbering plan, and for numbers whose national part is outside the plan's length range (the plan
 * does not describe them, so they are not rewritten to the national form — as on the backend).
 */
function toLocalNumber(e164: string, international: string, tenantCountryCode: string): string {
  const plan = getNationalNumberingPlan(tenantCountryCode);
  if (!plan) {
    return international;
  }

  const digits = e164.slice(1);
  if (!digits.startsWith(plan.countryCode)) {
    return international;
  }

  const nationalLength = digits.length - plan.countryCode.length;
  if (
    nationalLength < plan.nationalNumberMinLength ||
    nationalLength > plan.nationalNumberMaxLength
  ) {
    return international;
  }

  const prefix = `+${plan.countryCode} `;
  if (!international.startsWith(prefix)) {
    return international;
  }

  const separator = plan.trunkPrefix === '0' ? '' : ' ';

  return `${plan.trunkPrefix}${separator}${international.slice(prefix.length)}`;
}
